"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { toast } from "sonner";
import { selectAuthToken, useAuthStore } from "../../store/useAuth";
import { handleAuthError } from "../../utils/auth-error-handler";
import { getAuthHeaders, isPublicEndpoint } from "../../utils/auth-utils";
import { buildApiUrl, MutationConfig } from "./hook-utils";

const DEFAULT_API_BASE = "http://localhost:8090/api/v1/development";
const CLOSED_DATABASE_CONNECTION_ERROR = "Server has closed the connection";
const CLOSED_DATABASE_CONNECTION_RETRY_COUNT = 2;
const CLOSED_DATABASE_CONNECTION_RETRY_DELAY_MS = 1000;

const isClosedDatabaseConnectionError = (error: unknown): boolean => {
  const errorData = (error as { data?: { rawErrors?: unknown[] } })?.data;

  return (
    Array.isArray(errorData?.rawErrors) &&
    errorData.rawErrors.some(
      (rawError) =>
        typeof rawError === "string" &&
        rawError.includes(CLOSED_DATABASE_CONNECTION_ERROR)
    )
  );
};

interface UseFetchConfig {
  onSuccess?: (data: any) => void;
  onError?: (error: unknown) => void;
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  retry?: boolean | number;
  queryParams?: Record<string, any>;
}

const useFetch = <T>(endpoint?: string, config?: UseFetchConfig) => {
  const token = useAuthStore(selectAuthToken);

  const url = useMemo(() => {
    if (!endpoint) return null;

    // Use the same URL building logic as hook-utils.ts
    const baseUrl = process.env.REACT_APP_API_BASE || DEFAULT_API_BASE;

    if (!baseUrl) {
      console.error("REACT_APP_API_BASE environment variable is required");
      return null;
    }

    // Clean up endpoint to avoid double slashes
    const cleanEndpoint = endpoint.startsWith("/")
      ? endpoint.slice(1)
      : endpoint;
    const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;

    let finalUrl = `${cleanBaseUrl}/${cleanEndpoint}`;

    // Add query parameters if provided
    if (config?.queryParams) {
      const searchParams = new URLSearchParams();
      Object.entries(config.queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        finalUrl += `?${queryString}`;
      }
    }

    return finalUrl;
  }, [endpoint, config?.queryParams]);

  const query = useQuery<T>({
    queryKey: [endpoint, token, config?.queryParams],

    queryFn: async () => {
      if (!url) throw new Error("URL is not available");

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(endpoint || "", token),
        },
      });

      if (!response.ok) {
        // Try to parse error response from backend
        try {
          const errorText = await response.text();
          if (errorText) {
            const errorData = JSON.parse(errorText);
            const error = new Error(
              errorData.message || `HTTP error! status: ${response.status}`
            );
            (error as any).data = errorData;
            (error as any).status = response.status;
            (error as any).statusText = response.statusText;
            throw error;
          }
        } catch (parseError) {
          // If parsing fails, fall back to generic error
          if (
            parseError instanceof Error &&
            parseError.message !== `HTTP error! status: ${response.status}`
          ) {
            throw parseError; // Re-throw if it's our custom error
          }
        }

        // Create a more descriptive error for different status codes
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (response.status === 400) {
          errorMessage =
            "Неверный запрос. Проверьте данные и попробуйте снова.";
        } else if (response.status === 401) {
          errorMessage =
            "Необходима авторизация для выполнения этого действия.";
        } else if (response.status === 403) {
          errorMessage = "У вас нет прав для выполнения этого действия.";
        } else if (response.status === 404) {
          errorMessage = "Запрашиваемый ресурс не найден.";
        } else if (response.status === 409) {
          errorMessage =
            "Конфликт данных. Возможно, ресурс используется в других местах.";
        } else if (response.status === 422) {
          errorMessage = "Ошибка валидации данных.";
        } else if (response.status >= 500) {
          errorMessage = "Внутренняя ошибка сервера. Попробуйте позже.";
        }

        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).statusText = response.statusText;
        throw error;
      }

      const result = await response.json();

      // Handle CRM API response format
      if (result.success && result.data) {
        return result.data;
      }
      return result;
    },
    enabled: !!url && config?.enabled !== false,
    ...(config?.staleTime !== undefined && { staleTime: config.staleTime }),
    ...(config?.cacheTime !== undefined && { gcTime: config.cacheTime }),
    ...(config?.refetchOnWindowFocus !== undefined && {
      refetchOnWindowFocus: config.refetchOnWindowFocus,
    }),
    ...(config?.refetchOnReconnect !== undefined && {
      refetchOnReconnect: config.refetchOnReconnect,
    }),
    retry:
      config?.retry ??
      ((failureCount, error) =>
        failureCount < CLOSED_DATABASE_CONNECTION_RETRY_COUNT &&
        isClosedDatabaseConnectionError(error)),
    retryDelay: CLOSED_DATABASE_CONNECTION_RETRY_DELAY_MS,
  });

  // Handle success and error callbacks using useEffect
  useEffect(() => {
    if (query.isSuccess && query.data && config?.onSuccess) {
      config.onSuccess(query.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.data]); // Only depend on query state, not callback reference

  useEffect(() => {
    if (!query.isError) return;

    // Check if this is an auth error and handle logout
    handleAuthError(query.error);

    if (config?.onError) {
      config.onError(query.error);
      return;
    }

    const message =
      (query.error as any)?.message || "Не удалось загрузить данные";
    toast.error(message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]); // Only depend on query state, not callback reference

  return query;
};

// HTTP method-specific hooks
const useGet = <T>(endpoint?: string, config?: UseFetchConfig) => {
  return useFetch<T>(endpoint, config);
};

const usePut = <T, TVariables = any>(
  endpoint: string,
  config?: MutationConfig
) => {
  const token = useAuthStore(selectAuthToken);
  const queryClient = useQueryClient();

  return useMutation<T, Error, TVariables>({
    mutationFn: async (data: TVariables) => {
      try {
        const url = buildApiUrl(endpoint);

        if (!url) {
          throw new Error("URL is not available");
        }

        if (!token && !isPublicEndpoint(endpoint)) {
          throw new Error("Authentication token is required");
        }

        const response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(endpoint, token),
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          // Try to parse error response from backend
          try {
            const errorText = await response.text();
            if (errorText) {
              const errorData = JSON.parse(errorText);
              if (
                "success" in errorData &&
                !errorData.success &&
                "message" in errorData
              ) {
                const error = new Error(errorData.message);
                (error as any).status = response.status;
                (error as any).data = errorData;
                throw error;
              }
            }
          } catch (parseError) {
            // If it's our custom error, re-throw it
            if (
              parseError instanceof Error &&
              parseError.message !== `HTTP error! status: ${response.status}`
            ) {
              throw parseError;
            }
            // Otherwise, fall through to generic error
          }

          const error = new Error(`HTTP error! status: ${response.status}`);
          (error as any).status = response.status;
          throw error;
        }

        const result = await response.json();

        // Check for error in response body (even with 200 status)
        if ("success" in result && !result.success && "message" in result) {
          const error = new Error(result.message);
          (error as any).status = response.status;
          (error as any).data = result;
          throw error;
        }

        // Handle CRM API response format
        if (result.success && result.data) {
          return result.data;
        }
        return result;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [endpoint] });
      config?.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      // Check if this is an auth error and handle logout
      handleAuthError(error);

      if (config?.onError) {
        config.onError(error);
        return;
      }
      const message = (error as any)?.message || "Запрос не выполнен";
      toast.error(message);
    },
    ...(config?.onMutate && { onMutate: config.onMutate }),
  });
};

export { useFetch, useGet, usePut };
export default useFetch;
